import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { BLOG_POSTS, localized } from "../data/blog";

function dateFmt(iso, lang) {
  try {
    const d = new Date(iso);
    const locale = lang === "ar" ? "ar-EG" : lang === "en" ? "en-GB" : "de-DE";
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

function PostCard({ post, lang, t, isFeature }) {
  const [ref, visible] = useScrollReveal();
  const title = localized(post, lang, "title");
  const excerpt = localized(post, lang, "excerpt");
  return (
    <Link
      to={`/blog/${post.slug}`}
      ref={ref}
      className={`card-lift bg-white border border-neutral-200 rounded-sm overflow-hidden flex ${isFeature ? "flex-col md:flex-row" : "flex-col"} sr sr-scale ${visible ? "is-visible" : ""}`}
      data-testid={`blog-card-${post.slug}`}
    >
      <div className={`${isFeature ? "md:w-1/2" : ""} relative aspect-[16/10] bg-neutral-200 overflow-hidden`}>
        <img src={post.cover} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <span className="absolute top-4 start-4 px-2.5 py-1 bg-[#FF5A00] text-white text-[10px] font-bold tracking-widest uppercase rounded-sm">
          {t.services?.[post.category]?.name?.split(" ")[0] || post.category}
        </span>
      </div>
      <div className="p-6 lg:p-8 flex-1 flex flex-col">
        <div className="flex items-center gap-4 text-xs text-neutral-500 mb-3 font-semibold">
          <span className="inline-flex items-center gap-1.5"><Calendar size={12} />{dateFmt(post.date, lang)}</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={12} />{post.readTime} {t.blog.min_read}</span>
        </div>
        <h3 className={`${isFeature ? "text-2xl lg:text-3xl" : "text-xl"} font-bold tracking-tight mb-3 leading-tight`}>{title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed flex-1">{excerpt}</p>
        <span className="inline-flex items-center gap-2 text-sm font-bold mt-6 text-[#FF5A00] group">
          {t.blog.read_more} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform rtl:rotate-180" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const { t, lang } = useLang();

  useEffect(() => {
    document.title = `${t.blog.page_title} — Talkani`;
    window.scrollTo(0, 0);
  }, [t.blog.page_title]);

  return (
    <div className="bg-[#F8F9FA] min-h-[60vh]">
      {/* Header */}
      <section className="bg-[#0A0B0D] text-white py-20 lg:py-28 grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.blog.eyebrow}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mb-6 max-w-4xl">{t.blog.page_title}</h1>
          <p className="text-lg text-neutral-300 max-w-2xl leading-relaxed">{t.blog.page_subtitle}</p>
        </div>
      </section>

      {/* Posts */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {BLOG_POSTS.length === 0 ? (
            <p className="text-neutral-500 text-center">{t.blog.empty}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {BLOG_POSTS.map((post, i) => (
                <div key={post.slug} className={i === 0 ? "md:col-span-12" : "md:col-span-6"}>
                  <PostCard post={post} lang={lang} t={t} isFeature={i === 0} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
